import { defineNuxtPlugin } from '#app';
import posthog, { type CaptureResult } from 'posthog-js';
import { getCookie } from '@/helpers/cookie';

function isCookieBannerElement(element: Record<string, any>) {
  const id = element.attr__id;
  if (typeof id === 'string' && id.startsWith('cky')) return true;

  const className = element.attr__class;
  if (typeof className === 'string' && /(^|\s)cky-/.test(className)) return true;

  return Object.keys(element).some(key => key.startsWith('attr__data-cky'));
}

function isCookieBannerAutocapture(event: CaptureResult) {
  if (event.event !== '$autocapture') return false;

  const elements: Array<Record<string, any>> = event.properties?.$elements || [];
  return elements.some(isCookieBannerElement);
}

export default defineNuxtPlugin(nuxtApp => {

  function hasConsentCookieAccepted() {
    const name = 'OptanonConsent';
    const regex = new RegExp(`(^| )${name}=([^;]+)`);
    const match = document.cookie.match(regex);
    if (match) {
      const params = Object.fromEntries(new URLSearchParams(match[2]));
      if (params.groups !== undefined) {
        return params.groups.search('C0002:1') !== -1;
      }
      return false;
    }
    return false;
  }

  const runtimeConfig = useRuntimeConfig();
  const posthogClient = posthog.init(runtimeConfig.public.posthogPublicKey, {
    api_host: runtimeConfig.public.posthogHost || 'https://app.posthog.com',
    persistence: (hasConsentCookieAccepted()) ? 'localStorage+cookie' : 'memory',
    capture_pageview: false, // we add manual pageview capturing below
    disable_web_experiments: false,
    before_send: (event) => {
      // skip CookieYes banner interactions (IAB TCF toggles flood autocapture)
      if (event && isCookieBannerAutocapture(event)) return null;
      return event;
    },
    loaded: (posthog) => {
      const posthogDistinctId = getCookie('posthog_distinct_id');
      if (posthogDistinctId) posthog.identify(posthogDistinctId);
      if (import.meta.env.MODE === 'development') posthog.debug();
    }
  });

  return {
    provide: {
      posthog: () => posthogClient
    }
  };
});
